import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { FloatingLabelSearchSelect } from '../elements/floatingLabelInput/FloatingLabelInput';
import Input from '../elements/input/Input';
import { Button } from 'antd';
import GenericTable from '../tables/GenericTable';
import {
  fetchMedics,
  getDoctorReferralChain,
  updateDoctorReferralChainEntry,
} from '../../api/medics-api';
import { applyReferralCode } from '../../api/user-api';
import { useInfiniteOptions, withSelectedOption } from '../../hooks/useInfiniteOptions';

/**
 * Shared referredByDoctor + referral-chain UI, reused by the admin doctor edit page
 * (mode="admin") and the doctor's own self-service referral modal (mode="self").
 *
 * referredByDoctor is set-once and then locked in both modes (matches the backend's
 * existing first-time-only guard) — admin picks another doctor directly by name, while
 * a doctor applies their own referral code (the same mechanism any user in the app uses),
 * since that's the only self-service path the backend already supports for this field.
 *
 * The chain viewer (index 0 = this doctor, then upline) is always shown; only mode="admin"
 * can edit each entry's percent — editing only ever affects this doctor's own chain
 * document, never any other doctor's.
 */
const DoctorReferralSection = ({
  doctor,
  mode = 'self',
  onReferredByDoctorChange,
}) => {
  const isAdmin = mode === 'admin';
  const doctorId = doctor?._id;
  const hasReferrer = !!doctor?.referredByDoctor;

  const [chain, setChain] = useState([]);
  const [activeLevels, setActiveLevels] = useState(null);
  const [loadingChain, setLoadingChain] = useState(false);
  const [editingPercent, setEditingPercent] = useState({});
  const [savingEntryId, setSavingEntryId] = useState(null);

  const [selectedReferrer, setSelectedReferrer] = useState(null);

  const [referralCode, setReferralCode] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [codeApplied, setCodeApplied] = useState(false);

  const loadChain = async () => {
    if (!doctorId) return;
    setLoadingChain(true);
    const { data, success } = await getDoctorReferralChain(doctorId);
    setLoadingChain(false);
    if (!success) return;
    setChain(data?.chain || []);
    setActiveLevels(data?.activeLevels ?? null);
  };

  const fetchDoctorPage = useCallback(
    async (page, limit, search) => {
      const { data, success } = await fetchMedics({ page, limit, search: search || null });
      if (!success) return null;
      return {
        items: (data?.doctors || []).filter((d) => d._id !== doctorId),
        totalPages: data?.pagination?.totalPages,
      };
    },
    [doctorId],
  );
  const {
    items: loadedDoctors,
    loading: doctorsLoading,
    onSearch: setDoctorSearch,
    onPopupScroll: onDoctorsScroll,
  } = useInfiniteOptions(fetchDoctorPage, {
    enabled: isAdmin && !hasReferrer,
    reloadKey: doctorId,
  });
  const doctors = withSelectedOption(loadedDoctors, selectedReferrer, '_id');

  useEffect(() => {
    loadChain();
  }, [doctorId]);

  const handleSelectReferrer = (val) => {
    const doctorMatch = doctors.find((d) => d._id === val) || null;
    setSelectedReferrer(doctorMatch);
    onReferredByDoctorChange?.(val || null);
  };

  const handleApplyCode = async () => {
    if (!referralCode.trim()) {
      toast.error('Unesite referral kod mentora.');
      return;
    }
    setApplyingCode(true);
    const { success, message } = await applyReferralCode(referralCode.trim());
    setApplyingCode(false);
    if (!success) {
      toast.error(message || 'Neispravan referral kod.');
      return;
    }
    toast.success('Referral kod je uspešno primenjen.');
    setCodeApplied(true);
    loadChain();
  };

  const handlePercentChange = (entryDoctorId, value) => {
    setEditingPercent((prev) => ({ ...prev, [entryDoctorId]: value }));
  };

  const handleSavePercent = async (entryDoctorId) => {
    const value = editingPercent[entryDoctorId];
    const percent = Number(value);
    if (Number.isNaN(percent) || percent < 0) {
      toast.error('Unesite ispravan procenat.');
      return;
    }

    // Mirrors the backend's cap (referral-commission.service.ts's
    // updateChainEntryPercent): the sum of the chain's active-window (first
    // activeLevels) percentages can't exceed 16%. Checked client-side too so a
    // doomed edit doesn't round-trip to the server first.
    const entryIndex = chain.findIndex(
      (e) => (e?.doctor?._id || e?.doctor) === entryDoctorId
    );
    if (activeLevels != null && entryIndex !== -1 && entryIndex < activeLevels) {
      const activeWindowSum = chain
        .slice(0, activeLevels)
        .reduce((sum, e, i) => sum + (i === entryIndex ? percent : e.percent), 0);
      if (activeWindowSum > 16) {
        toast.error('Zbir procenata prvih aktivnih nivoa ne sme preći 16%.');
        return;
      }
    }

    setSavingEntryId(entryDoctorId);
    const { success, message } = await updateDoctorReferralChainEntry(
      doctorId,
      entryDoctorId,
      percent
    );
    setSavingEntryId(null);
    if (!success) {
      toast.error(message || 'Došlo je do greške pri čuvanju procenta.');
      return;
    }
    toast.success('Procenat je sačuvan.');
    setEditingPercent((prev) => {
      const next = { ...prev };
      delete next[entryDoctorId];
      return next;
    });
    loadChain();
  };

  const chainRows = chain.map((entry, index) => {
    const entryDoctorId = entry?.doctor?._id || entry?.doctor;
    const name = entry?.doctor?.firstName
      ? `${entry.doctor.firstName} ${entry.doctor.lastName}`
      : '-';
    const level = index + 1;
    return {
      _id: entryDoctorId || `row-${index}`,
      level,
      isActive: activeLevels == null || level <= activeLevels,
      name,
      percent: entry.percent,
      entryDoctorId,
    };
  });

  const chainColumns = [
    {
      key: 'level',
      label: 'Nivo',
      render: (val, row) => {
        const base = val === 1 ? 'Mentor (nivo 1)' : `Nivo ${val}`;
        return row.isActive ? base : `${base} (neaktivan)`;
      },
    },
    { key: 'name', label: 'Ime i prezime' },
    {
      key: 'percent',
      label: 'Procenat provizije',
      render: (val, row) => {
        if (!isAdmin) {
          return <span>{val}%</span>;
        }
        const editingValue = editingPercent[row.entryDoctorId];
        const isEditing = editingValue !== undefined;
        return (
          <div className="chain_percent_cell">
            <input
              type="number"
              min={0}
              step="0.01"
              className="chain_percent_input"
              value={isEditing ? editingValue : val}
              onChange={(e) =>
                handlePercentChange(row.entryDoctorId, e.target.value)
              }
            />
            <span>%</span>
            <button
              type="button"
              className="link_btn chain_percent_save"
              style={{
                marginLeft: 0,
                opacity: isEditing ? 1 : 0,
                pointerEvents: isEditing ? 'auto' : 'none',
              }}
              disabled={!isEditing || savingEntryId === row.entryDoctorId}
              onClick={() => handleSavePercent(row.entryDoctorId)}
            >
              <span>
                {savingEntryId === row.entryDoctorId ? 'Čuvanje...' : 'Sačuvaj'}
              </span>
            </button>
          </div>
        );
      },
    },
  ];

  // label is a plain string (used for the compact closed-select display);
  // optionRender below renders the richer two-line version inside the open dropdown.
  const doctorOptions = doctors.map((d) => ({
    value: d._id,
    label: `${d.firstName} ${d.lastName} — ${d.referralCode || '-'}`,
    doctor: d,
  }));

  const renderDoctorOption = (option) => {
    const d = option.data.doctor;
    return (
      <div className="doctor_option">
        <span className="doctor_option_name">
          {d.firstName} {d.lastName}
        </span>
        <span className="doctor_option_code">
          Referral kod mentora: {d.referralCode || '-'}
        </span>
      </div>
    );
  };

  return (
    <div className="doctor_referral_section">
      <div className="cell">
        {hasReferrer || codeApplied ? (
            
          <div className="referrer_locked">
            <p>Mentor preporučen od strane:</p>
            <span className="referrer_locked_name">
              {chain?.[1]?.doctor?.firstName
                ? `${chain[1].doctor.firstName} ${chain[1].doctor.lastName}`
                : 'Postavljeno'}
            </span>
            {chain?.[1]?.doctor?.referralCode && (
              <span className="referrer_locked_code">
                Referral kod mentora: {chain[1].doctor.referralCode}
              </span>
            )}
          </div>
        ) : isAdmin ? (
          <FloatingLabelSearchSelect
            label="Izaberite mentora"
            placeholder="Pretraži po imenu ili email-u..."
            value={selectedReferrer?._id}
            onChange={handleSelectReferrer}
            onSearch={setDoctorSearch}
            loading={doctorsLoading}
            onPopupScroll={onDoctorsScroll}
            options={doctorOptions}
            optionRender={renderDoctorOption}
          />
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              name="referralCode"
              id="referralCode"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              placeholder="Unesite referral kod mentora"
            />
            <Button
              className="primary_btn"
              type="button"
              onClick={handleApplyCode}
              disabled={applyingCode}
            >
              <span>{applyingCode ? 'Primena...' : 'Primeni kod'}</span>
            </Button>
          </div>
        )}
      </div>

      <div className="chain_viewer" style={{ marginTop: 24 }}>
        <p className="title">Referral lanac</p>
        {activeLevels != null && (
          <p style={{ fontSize: 12, color: '#667085', margin: '4px 0 12px' }}>
            Samo prvih {activeLevels} nivoa donosi proviziju u obračunu — niži
            nivoi su prikazani radi istorije lanca i ne utiču na trenutnu
            proviziju.
          </p>
        )}
        <GenericTable
          data={loadingChain ? [] : chainRows}
          columns={chainColumns}
          grayTheme={false}
          rowClassName={(row) => (row.isActive ? '' : 'chain-row--inactive')}
          emptyState={{
            icon: '',
            title: loadingChain ? 'Učitavanje...' : 'Nema podataka',
          }}
        />
      </div>
    </div>
  );
};

DoctorReferralSection.propTypes = {
  doctor: PropTypes.shape({
    _id: PropTypes.string,
    referredByDoctor: PropTypes.string,
  }),
  mode: PropTypes.oneOf(['admin', 'self']),
  onReferredByDoctorChange: PropTypes.func,
};

export default DoctorReferralSection;
